import React, { useEffect, useState, useRef } from "react";
import ChecklistModal from "../../components/standalonefs/ChecklistModal";
import ReferenceModal from "../../components/standalonefs/ReferenceModal";
import commoncss from "../../css/common.module.css";
import libcss from "../../css/library.module.css";
import swal from "sweetalert";
import "./ConsolFSTable.css"

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MenuIcon from "../../assets/icons/Menu.png";
import { ReactComponent as DelIcon } from "../../assets/icons/delete_red.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusprimary.svg";

const genObjectId = () => {
  return Array.from({ length: 24 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");
};

const isValueColumn = (col) => {
  const fieldLower = String(col.field || "").toLowerCase();
  const labelLower = String(col.label || "").toLowerCase();
  const colLower = fieldLower + " " + labelLower;
  const isAmountOrYear =
    colLower.includes("amount") ||
    colLower.includes("current year") ||
    colLower.includes("previous year") ||
    colLower.includes("currentyear") ||
    colLower.includes("previousyear") ||
    /\bcy\b/.test(colLower) ||
    /\bpy\b/.test(colLower);
  return (
    col.type === "number" ||
    col.align === "right" ||
    fieldLower.includes("year") ||
    labelLower.includes("year") ||
    isAmountOrYear ||
    fieldLower.includes("value") ||
    labelLower.includes("value")
  );
};


export default function ConsolFSTable({
  columns = [],
  rows = [],
  onRowsChange = () => {},
  onCellChangeImmediate = () => {},
  brandingClass = "ft-branding",
  emptyMessage = "No data for this category",
  containerWidth = "100%",
  redirectTo = () => {},
  checklistRef = () => {},
  tableTitle = null,
  tableId = null,
  tableLayout = "fixed",
  tableType = "table",
  deletePopulateFS = () => {},
  isNotesTable = false,
  isPrePopulate = false,
  isNotesView = false,
  activeCat = "",
  allowDragAndAdd = false,
  groupedColumns = null,
}) {
  const [localRows, setLocalRows] = useState(
    Array.isArray(rows) ? [...rows] : []
  );
  const [editing, setEditing] = useState(null); 
  const inputRef = useRef(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [viewReferenceModal, setViewReferenceModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [refrenceModalData, setReferenceModalData] = useState(null);
  
  useEffect(() => {
    setLocalRows(Array.isArray(rows) ? [...rows] : []);
    setEditing(null);
  }, [rows]);

  useEffect(() => {
    if (editing && inputRef.current) {
      try {
        inputRef.current.focus();
        inputRef.current.select?.();
      } catch (e) {}
    }
  }, [editing]);

  const startEdit = (rowIdx, col) => {
    const row = localRows[rowIdx];
    const canEdit = col.editable || (allowDragAndAdd && row?.isNewRow);
    if (!canEdit) return;
    if (col.type === "checkbox") return;
    if ((row?.lineType || "").toLowerCase() === "header") return;
    setEditing({ rowIdx, field: col.field, col });
  };

  const commitValue = (valueStr) => {
    if (!editing) return;
    const { rowIdx, field, col } = editing;
    const updated = [...localRows];
    let newVal = valueStr;

    if (col.type === "number") {
      newVal = valueStr === "" ? null : Number(valueStr);
      if (isNaN(newVal)) newVal = null;
    } else if (col.type === "array") {
      newVal = String(valueStr)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    updated[rowIdx] = { ...updated[rowIdx], [field]: newVal };
    setLocalRows(updated);
    setEditing(null);

    if (!updated[rowIdx]?.isNewRow) {
      try {
        onRowsChange(updated, tableId);
      } catch (e) {
        console.error("onRowsChange error", e);
      }
    }
  };

  // Emit single-object immediate change: { _id, key, value }
  const emitImmediateChange = (rowIdx, key, value) => {
    const row = localRows[rowIdx] ?? {};
    if (row.isNewRow) return;
    const id = row._id ?? rowIdx;
    try {
      onCellChangeImmediate({ _id: id, key, value, tableId });
    } catch (e) {
      console.error("onCellChangeImmediate error", e);
    }
  };

  // Handles single-value checkbox option columns (field: "value", optionValue: "Y"/"N"/"NA")
  const toggleSingleValueCheckbox = (rowIdx, col, checked) => {
    if (!col.optionValue) {
      console.warn("[ConsolFSTable] checkbox column missing optionValue", col);
      return;
    }
    const updated = [...localRows];
    const row = { ...updated[rowIdx] };

    if (checked) {
      row[col.field] = col.optionValue;
    } else {
      row[col.field] = null;
    }

    updated[rowIdx] = row;
    setLocalRows(updated);

    // immediate emit single-object diff
    emitImmediateChange(rowIdx, col.field, row[col.field]);

    if (!row.isNewRow) {
      try {
        onRowsChange(updated, tableId);
      } catch (e) {
        console.error("onRowsChange error", e);
      }
    }
  };

  const cancel = () => setEditing(null);

  const droppableId = `consol-fs-rows-${tableId || "default"}`;

  const displayValue = (row, field, col) => {
    if (row.level === 1 && row.lineType !== "formula") {
      const fieldLower = String(field || "").toLowerCase();
      if (fieldLower.includes("currentyear") || fieldLower.includes("previousyear")) {
        return "";
      }
    }
    const v = row[field];
    const type = col?.type;

    if (v == null || v === "") {
      if (row.isNewRow) {
        return isValueColumn(col) ? "0" : "Enter text";
      }
      return "";
    }

    if (Array.isArray(v)) return v.join(", ");

    if (type === "number" || isValueColumn(col)) {
      const num = Math.round(Number(v));
      if (isNaN(num)) return String(v);

      if (num < 0) {
        return "(" + Math.abs(num).toLocaleString("en-IN") + ")";
      }
      return num.toLocaleString("en-IN");
    }

    if (type === "checkbox") return "";

    return String(v);
  };

  const totalMinWidthPx = groupedColumns
  ? (groupedColumns.particular.width || 340) +
    groupedColumns.companies.reduce((sum, c) => sum + (c.width || 150) * 2, 0) +
    (groupedColumns.references ? (groupedColumns.references.width || 300) : 0)
  : columns.reduce((sum, c) => {
      const w = c.minWidth ?? c.width;
      const val = typeof w === "number" ? w : 0;
      return sum + val;
    }, 0);


  const normalizedContainerWidth =
    typeof containerWidth === "number"
      ? `${containerWidth}px`
      : containerWidth || "100%";

const isSocieTable =
  columns?.some(
    (c) =>
      c.field?.toLowerCase() === "particular" &&
      c.width &&
      Number(c.width) >= 400
  );

const appliedTableLayout = isSocieTable ? "auto" : tableLayout;

  const handleChecklistModal = (data) => {
    setModalData([]);
    const modalDataObj = {
      header: data.reference,
      libRef: data.libraryReference,
    };
    setModalData(modalDataObj);
    setShowChecklistModal(true);
  };

  const handleReferenceModal = (data) => {
    setReferenceModalData(data);
    console.log("handleReferenceModal data", data);

    setViewReferenceModal(true);
  };

  const addNewRow = () => {
    const newRowId = genObjectId();
    const newRow = {
      _id: newRowId,
      isNewRow: true,
      lineType: "row",
    };

    columns.forEach((c) => {
      newRow[c.field] = "";
    });

    setLocalRows([...localRows, newRow]);
  };

  const isHeaderRow = (row) =>
    (row?.lineType || "").toLowerCase() === "header";

  const mergeReorderedDataRows = (dataRows) => {
    let dataIdx = 0;
    return localRows.map((row) =>
      isHeaderRow(row) ? row : dataRows[dataIdx++]
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const dataRows = localRows.filter((row) => !isHeaderRow(row));
    const items = Array.from(dataRows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const merged = mergeReorderedDataRows(items);
    setLocalRows(merged);
    try {
      onRowsChange(merged, tableId);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  const handleDeleteFSTable = () => {
    swal({
      title: "Confirm deletion of linked register data?",
      text: "Once deleted, you will not be able to recover this!",
      icon: "warning",
      buttons: {
        cancel: "Cancel",
        confirm: {
          text: "Delete",
          value: true,
          visible: true,
          className: "swal-delete-btn",
          closeModal: true,
        },
      },
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        await deletePopulateFS(tableId);
        swal("Deleted successfully!", "", "success");
      }
    });
  };
  return (
    <div
      style={{
        width: normalizedContainerWidth,
        //overflowX: "auto",
        marginTop: (isPrePopulate && activeCat === "socie") ? "16px" : "20px",
      }}
      id={tableId}
    > 
      <div className="ft-table-scroll"  
       style={{
        borderRadius: isNotesTable ? "10px" : "12px",
        border: "1px solid #ccd5d3",
        borderCollapse: "collapse",
        overflowX: "auto",    
        overflowY: isPrePopulate || isSocieTable ? (isNotesView ? "visible" : "auto") : undefined,
        maxHeight: isPrePopulate || isSocieTable ? (isNotesView ? "none" : (activeCat === "BS notes" || activeCat === "PL notes" ? "calc(100vh - 130px)" : "calc(100vh - 90px)")) : undefined,
        position: "relative",
       }}>
        <table
          className={`ft-table ${brandingClass} ft-scrTable`}
          style={{
            minWidth: `${totalMinWidthPx}px`,
            borderCollapse: "collapse",
            tableLayout: appliedTableLayout,
          }}
        >
          <thead style={isPrePopulate || isSocieTable ? { position: "sticky", top: 0, zIndex: 150, backgroundColor: "#fff" } : undefined}>
  {tableTitle && (
    <tr className="ft-table-title-row" style={isPrePopulate || isSocieTable ? { position: "sticky", top: 0, zIndex: 151 } : undefined}>
      <th
        colSpan={
          groupedColumns
            ? 1 + groupedColumns.companies.length * 2 + (groupedColumns.references ? 1 : 0)
            : columns.length
        }
        style={isPrePopulate || isSocieTable ? { position: "sticky", top: 0, zIndex: 151, background: "#ffffff" } : undefined}
      >
        <div className="ft-table-title" style={{ position: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "sticky" : "relative", left: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "10px" : undefined, width: "max-content" }}>
          <span>{tableTitle}</span>
          {tableType === "link register" && (
            <DelIcon onClick={handleDeleteFSTable} />
          )}
        </div>
      </th>
    </tr>
  )}

  {groupedColumns ? (
    <>
      <tr>
        <th
          rowSpan={2}
          style={{
            width: `${groupedColumns.particular.width}px`,
            minWidth: `${groupedColumns.particular.width}px`,
            textAlign: "left",
            verticalAlign: "middle",
            borderRight: "1px solid #ccd5d3",
            borderBottom: "1px solid #ccd5d3",
            position: "sticky",
            top: tableTitle ? 32 : 0,
            left: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 0 : undefined,
            zIndex: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 155 : 105,
            backgroundColor: "#eef6ef",
          }}
        >
          Particular
        </th>
        {groupedColumns.companies.map((company, i) => (
          <th
            key={`grp-${i}`}
            colSpan={2}
            style={{
              textAlign: "center",
              borderRight: "1px solid #ccd5d3",
              borderBottom: "1px solid #ccd5d3",
              position: "sticky",
              top: tableTitle ? 32 : 0,
              zIndex: 105,
              backgroundColor: "#eef6ef",
              fontWeight: 600,
            }}
          >
            {company.title}
          </th>
        ))}
        {groupedColumns.references && (
          <th
            rowSpan={2}
            style={{
              width: `${groupedColumns.references.width}px`,
              minWidth: `${groupedColumns.references.width}px`,
              textAlign: "left",
              verticalAlign: "middle",
              borderBottom: "1px solid #ccd5d3",
              position: "sticky",
              top: tableTitle ? 32 : 0,
              zIndex: 105,
              backgroundColor: "#eef6ef",
            }}
          >
            References
          </th>
        )}
      </tr>
      <tr>
        {groupedColumns.companies.map((company, i) => (
          <React.Fragment key={`sub-${i}`}>
            <th
              style={{
                width: `${company.width}px`,
                minWidth: `${company.width}px`,
                textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
                borderRight: (activeCat === "BS notes" || activeCat === "PL notes") ? "none" : "1px solid #ccd5d3",
                borderBottom: "1px solid #ccd5d3",
                position: "sticky",
                top: tableTitle ? 64 : 32,
                zIndex: 105,
                backgroundColor: "#eef6ef",
                fontWeight: 700,
                fontSize: "12px",
              }}
            >
              {(activeCat === "BS notes" || activeCat === "PL notes") ? <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>CY</div> : "CY"}
            </th>
            <th
              style={{
                width: `${company.width}px`,
                minWidth: `${company.width}px`,
                textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
                borderRight: "none",
                borderBottom: "1px solid #ccd5d3",
                position: "sticky",
                top: tableTitle ? 64 : 32,
                zIndex: 105,
                backgroundColor: "#eef6ef",
                fontWeight: 700,
                fontSize: "12px",
              }}
            >
              {(activeCat === "BS notes" || activeCat === "PL notes") ? <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>PY</div> : "PY"}
            </th>
          </React.Fragment>
        ))}
      </tr>
    </>
  ) : (
    <tr>
      {columns.map((c, i) => {
        const colWidth = c.width || (c.field === "baseCurrentYear" ? 160 : undefined);
        const widthStyle = colWidth ? (typeof colWidth === "number" ? `${colWidth}px` : colWidth) : undefined;
        const isSocie = activeCat === "socie";
        const titleOffset = tableTitle ? 32 : 0;

        return (
          <th
            key={i}
            style={{
              width: widthStyle,
              minWidth: widthStyle,
              maxWidth: widthStyle,
              textAlign: c.align || "left",
              paddingLeft: i === 0 && allowDragAndAdd ? "28px" : undefined,
              ...((isPrePopulate || isSocieTable) && {
                verticalAlign: "middle",
                borderRight: isSocie ? "none" : "1px solid #ccd5d3",
                borderBottom: "1px solid #ccd5d3",
                position: "sticky",
                top: titleOffset,
                zIndex: i === 0 && isSocieTable ? 155 : 105,
                backgroundColor: "#eef6ef",
                ...(i === 0 && isSocieTable && { left: 0 })
              })
            }}
            className={
              c.align === "right"
                ? "ft-align-right"
                : c.align === "center"
                ? "ft-align-center"
                : "ft-align-left"
            }
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : c.align === "center" ? "center" : "flex-start",
              width: "100%"
            }}>
              {c.label}
            </div>
          </th>
        );
      })}
    </tr>
  )}
</thead>


          {allowDragAndAdd ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId={droppableId} type="ROW">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {!localRows || localRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{ textAlign: "center", padding: 20 }}
                        >
                          {emptyMessage}
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        let dragIndex = 0;
                        return localRows.map((row, rIdx) => {
                        const rowKey = row._id ?? rIdx;

                        if (isHeaderRow(row)) {
                          return (
                            <tr key={rowKey}>
                              <td
                                colSpan={columns.length}
                                style={{
                                  background: "#eaeeed",
                                  borderCollapse: "collapse",
                                  color: "#33584e",
                                  fontWeight: 600,
                                  textAlign: "left",
                                  borderTopLeftRadius: tableTitle ? 0 : undefined,
                                  borderTopRightRadius: tableTitle ? 0 : undefined,
                                }}
                              >
                                {row.particular}
                              </td>
                            </tr>
                          );
                        }

                        const normalize = (str) =>
                          String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
                        const rowId = normalize(row.particular);
                        const draggableId = `${droppableId}-${rowKey}`;
                        const currentDragIndex = dragIndex++;

                        return (
                       <Draggable
  key={rowKey}
  draggableId={draggableId}
  index={currentDragIndex}
>
  {(dragProvided) => (
    <tr
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      id={rowId}
      style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined, backgroundColor: row.level === 1 ? "#eaeeed" : undefined }}
    >
      {groupedColumns ? (
        <>
          <td
            style={{
              position: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "sticky" : "static",
              left: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 0 : undefined,
              zIndex: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 100 : undefined,
              backgroundColor: row.level === 1 ? "#eaeeed" : ((isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "#fff" : undefined),
              width: `${groupedColumns.particular.width}px`,
              minWidth: `${groupedColumns.particular.width}px`,
              textAlign: "left",
              borderRight: "none",
            }}
          >
            {row.particular ?? ""}
          </td>

          {groupedColumns.companies.map((company, ci) => (
            <React.Fragment key={`cell-${ci}`}>
              <td
                style={{
                  width: `${company.width}px`,
                  minWidth: `${company.width}px`,
                  textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
                  borderRight: "1px solid #eaeeed",
                }}
              >
                {(() => {
                  if (row.level === 1 && row.lineType !== "formula") return "";
                  const v = row[company.cyField];
                  if (v == null || v === "") return "";
                  const num = Number(v);
                  if (isNaN(num)) return String(v);
                  const formatted = num < 0
                    ? "(" + Math.abs(num).toLocaleString("en-IN") + ")"
                    : num.toLocaleString("en-IN");
                  if (activeCat === "BS notes" || activeCat === "PL notes") {
                    return <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>{formatted}</div>;
                  }
                  return formatted;
                })()}
              </td>

              <td
                style={{
                  width: `${company.width}px`,
                  minWidth: `${company.width}px`,
                  textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
                  borderRight: "none",
                }}
              >
                {(() => {
                  if (row.level === 1 && row.lineType !== "formula") return "";
                  const v = row[company.pyField];
                  if (v == null || v === "") return "";
                  const num = Number(v);
                  if (isNaN(num)) return String(v);
                  const formatted = num < 0
                    ? "(" + Math.abs(num).toLocaleString("en-IN") + ")"
                    : num.toLocaleString("en-IN");
                  if (activeCat === "BS notes" || activeCat === "PL notes") {
                    return <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>{formatted}</div>;
                  }
                  return formatted;
                })()}
              </td>
            </React.Fragment>
          ))}

          <td
            style={{
              width: `${groupedColumns.references.width}px`,
              minWidth: `${groupedColumns.references.width}px`,
              textAlign: "left",
            }}
          >
            {Array.isArray(row.references)
              ? row.references.map((item, index) => (
                  <span key={index}>
                    <span
                      onClick={() => handleReferenceModal(item)}
                      style={{
                        cursor: "pointer",
                        marginRight: "8px",
                        textDecoration: "underline",
                      }}
                    >
                      {item}
                    </span>
                    {index < row.references.length - 1 && ", "}
                  </span>
                ))
              : row.references ?? ""}
          </td>
        </>
      ) : (
        <>
          {columns.map((col, cIdx) => {
            const isEditing =
              editing &&
              editing.rowIdx === rIdx &&
              editing.field === col.field;

            const inputAlignClass =
              col.field === "notes"
                ? "ft-input-center"
                : col.align === "right"
                ? "ft-input-right"
                : col.align === "center"
                ? "ft-input-center"
                : "ft-input-left";

            const cellText = displayValue(row, col.field, col);

            const isEmptyPlaceholder =
              row.isNewRow &&
              (row[col.field] == null || row[col.field] === "");

            const handleCellClick = () => {
              if (col.type === "checkbox") return;
              startEdit(rIdx, col);
            };

            const colWidth =
              col.width ||
              (col.field === "baseCurrentYear" ? 160 : undefined);

            const widthStyle = colWidth
              ? typeof colWidth === "number"
                ? `${colWidth}px`
                : colWidth
              : undefined;

            return (
              <td
                key={cIdx}
                style={{
                  position: cIdx === 0 && isSocieTable ? "sticky" : (cIdx === 0 ? "relative" : "static"),
                  left: cIdx === 0 && isSocieTable ? 0 : undefined,
                  zIndex: cIdx === 0 && isSocieTable ? 100 : undefined,
                  backgroundColor: row.level === 1 ? "#eaeeed" : (cIdx === 0 && isSocieTable ? "#fff" : undefined),
                  width: widthStyle,
                  minWidth: widthStyle,
                  maxWidth: widthStyle,
                  paddingLeft:
                    cIdx === 0 && allowDragAndAdd ? "28px" : undefined,
                }}
                className={`${
                  col.align === "right"
                    ? "ft-align-right"
                    : col.align === "center"
                    ? "ft-align-center"
                    : "ft-align-left"
                } ${isEditing ? "ft-editing" : ""}`}
                onClick={handleCellClick}
                onDoubleClick={handleCellClick}
              >
                {cIdx === 0 && allowDragAndAdd && (
                  <div
                    {...dragProvided.dragHandleProps}
                    className="ft-row-drag-handle"
                    style={{
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "14px",
                      height: "20px",
                    }}
                    title="Drag to reorder"
                  >
                    <img
                      src={MenuIcon}
                      alt="drag"
                      style={{ width: 9, height: 13 }}
                    />
                  </div>
                )}

                {col.type === "popup" && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start", textAlign: col.align === "right" ? "right" : col.align === "center" ? "center" : "left", width: "100%" }}>
                    <span className={commoncss.clickable} onClick={() => { handleChecklistModal(row); }}>{row[col.field]}</span>
                  </div>
                )}
                {col.type === "checklistref" && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start", textAlign: col.align === "right" ? "right" : col.align === "center" ? "center" : "left", width: "100%" }}>
                    {Array.isArray(row[col.field]) ? row[col.field].map((item, index) => (
                      <span key={index}><span onClick={() => { handleReferenceModal(item); }} style={{ cursor: "pointer", marginRight: "8px", textDecoration: "underline" }}>{item}</span>{index < row[col.field].length - 1 && ", "}</span>
                    )) : row[col.field] ?? ""}
                  </div>
                )}
                {col.type === "redirect" && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start", textAlign: col.align === "right" ? "right" : col.align === "center" ? "center" : "left", width: "100%" }}>
                    {row[col.field] ? (
                      <span style={{ color: "#205a46", textDecoration: "underline", cursor: "pointer", fontWeight: 600 }} onClick={() => { redirectTo(row[col.field], row); }}>{row[col.field]}</span>
                    ) : (
                      <span style={{ color: "#ccc" }}>{row.level === 1 ? "" : "–"}</span>
                    )}
                  </div>
                )}
                {col.type === "checkbox" ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start", width: "100%" }}>
                    <input name={`chk_${rowKey}_${col.field}_${col.optionValue}`} className="ft-checkbox" type="checkbox" checked={row[col.field] === col.optionValue} onChange={(e) => { const checked = e.target.checked; toggleSingleValueCheckbox(rIdx, col, checked); }} onClick={(e) => e.stopPropagation()} aria-label={`${col.label} checkbox row ${rIdx}`} />
                  </div>
                ) : (
                  <>
                    {col.type !== "redirect" && col.type !== "popup" && col.type !== "checklistref" && (
                      <div style={{ visibility: isEditing ? "hidden" : "visible", textAlign: col.align || "left", width: "100%" }}>
                        <span style={{
                          color: isEmptyPlaceholder ? "#aaa" : col.field === "notes" ? "#205a46" : "inherit",
                          fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                          fontWeight: col.field === "notes" ? 700 : "inherit",
                          textDecoration: col.field === "notes" && !isEmptyPlaceholder ? "underline" : "none",
                        }}>{cellText}</span>
                      </div>
                    )}
                    {isEditing && col.type !== "redirect" && col.type !== "popup" && col.type !== "checklistref" && (
                      <input ref={inputRef} className={`ft-edit-input ${inputAlignClass}`} type={col.type === "number" ? "number" : "text"} defaultValue={col.type === "array" && Array.isArray(row[col.field]) ? row[col.field].join(", ") : row[col.field] ?? ""} placeholder={row.isNewRow ? isValueColumn(col) ? "0" : "Enter text" : col.placeholder ?? ""} onBlur={(e) => commitValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitValue(e.target.value); if (e.key === "Escape") cancel(); }} onKeyUp={(e) => { const raw = e.target.value; let outgoing = raw; if (col.type === "number") { const n = raw === "" ? null : Number(raw); outgoing = isNaN(n) ? null : n; } else if (col.type === "array") { outgoing = String(raw).split(",").map((v) => v.trim()).filter(Boolean); } const updated = [...localRows]; updated[rIdx] = { ...updated[rIdx], [col.field]: outgoing }; setLocalRows(updated); emitImmediateChange(rIdx, col.field, outgoing); }} />
                    )}
                  </>
                )}
              </td>
            );
          })}
        </>
      )}
    </tr>
  )}
</Draggable>
                        );
                      });
                      })()
                    )}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
          <tbody>
            {!localRows || localRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: 20 }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              localRows.map((row, rIdx) => {
                const rowKey = row._id ?? rIdx;

                if ((row.lineType || "").toLowerCase() === "header") {
                  return (
                    <tr key={rowKey}>
                      <td
                        colSpan={columns.length}
                        style={{
                          background: "#eaeeed",
                          borderCollapse: "collapse",
                          color: "#33584e",
                          fontWeight: 600,
                          textAlign: "left",
                          borderTopLeftRadius: tableTitle ? 0 : undefined,
                          borderTopRightRadius: tableTitle ? 0 : undefined,
                        }}
                      >
                        {row.particular}
                      </td>
                    </tr>
                  );
                }

                const normalize = (str) => String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
                const rowId = normalize(row.particular);

                return (
  <tr key={rowKey} id={rowId} style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined, backgroundColor: row.level === 1 ? "#eaeeed" : undefined }}>
    {groupedColumns ? (
      <>
        {/* Particular cell */}
        <td style={{
          position: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "sticky" : "static",
          left: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 0 : undefined,
          zIndex: (isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? 100 : undefined,
          backgroundColor: row.level === 1 ? "#eaeeed" : ((isSocieTable || activeCat === "BS notes" || activeCat === "PL notes") ? "#fff" : undefined),
          width: `${groupedColumns.particular.width}px`,
          minWidth: `${groupedColumns.particular.width}px`,
          textAlign: "left",
          borderRight: "none",
        }}>
          {row.particular ?? ""}
        </td>
        {/* CY | PY cells per company */}
        {groupedColumns.companies.map((company, ci) => (
          <React.Fragment key={`cell-${ci}`}>
            <td style={{
              width: `${company.width}px`,
              minWidth: `${company.width}px`,
              textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
              borderRight: (activeCat === "BS notes" || activeCat === "PL notes") ? "none" : "1px solid #ccd5d3",
            }}>
              {(() => {
                if (row.level === 1 && row.lineType !== "formula") return "";
                const v = row[company.cyField];
                if (v == null || v === "") return "";
                const num = Number(v);
                if (isNaN(num)) return String(v);
                const formatted = num < 0 ? "(" + Math.abs(num).toLocaleString("en-IN") + ")" : num.toLocaleString("en-IN");
                if (activeCat === "BS notes" || activeCat === "PL notes") {
                  return <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>{formatted}</div>;
                }
                return formatted;
              })()}
            </td>
            <td style={{
              width: `${company.width}px`,
              minWidth: `${company.width}px`,
              textAlign: (activeCat === "BS notes" || activeCat === "PL notes") ? "center" : "right",
              borderRight: "none",
            }}>
              {(() => {
                if (row.level === 1 && row.lineType !== "formula") return "";
                const v = row[company.pyField];
                if (v == null || v === "") return "";
                const num = Number(v);
                if (isNaN(num)) return String(v);
                const formatted = num < 0 ? "(" + Math.abs(num).toLocaleString("en-IN") + ")" : num.toLocaleString("en-IN");
                if (activeCat === "BS notes" || activeCat === "PL notes") {
                  return <div style={{ display: "inline-block", width: "100px", textAlign: "right" }}>{formatted}</div>;
                }
                return formatted;
              })()}
            </td>
          </React.Fragment>
        ))}
        {/* References cell */}
        {groupedColumns.references && (
          <td style={{
            width: `${groupedColumns.references.width}px`,
            minWidth: `${groupedColumns.references.width}px`,
            textAlign: "left",
          }}>
            {Array.isArray(row.references)
              ? row.references.map((item, index) => (
                  <span key={index}>
                    <span
                      onClick={() => { handleReferenceModal(item); }}
                      style={{ cursor: "pointer", marginRight: "8px", textDecoration: "underline" }}
                    >
                      {item}
                    </span>
                    {index < row.references.length - 1 && ", "}
                  </span>
                ))
              : row.references ?? ""}
          </td>
        )}
      </>
    ) : (
      <>
        {columns.map((col, cIdx) => {
                      const isEditing =
                        editing &&
                        editing.rowIdx === rIdx &&
                        editing.field === col.field;
                      const inputAlignClass =
                        col.field === "notes"
                          ? "ft-input-center"
                          : col.align === "right"
                          ? "ft-input-right"
                          : col.align === "center"
                          ? "ft-input-center"
                          : "ft-input-left";

                      const cellText = displayValue(row, col.field, col);
                      const isEmptyPlaceholder =
                        row.isNewRow &&
                        (row[col.field] == null || row[col.field] === "");

                      const handleCellClick = () => {
                        if (col.type === "checkbox") return;
                        startEdit(rIdx, col);
                      };

                      const colWidth = col.width || (col.field === "baseCurrentYear" ? 160 : undefined);
                      const widthStyle = colWidth ? (typeof colWidth === "number" ? `${colWidth}px` : colWidth) : undefined;

                      return (
                      <td
                      key={cIdx}
                      style={{
                          position: cIdx === 0 && isSocieTable ? "sticky" : "static",
                          left: cIdx === 0 && isSocieTable ? 0 : undefined,
                          zIndex: cIdx === 0 && isSocieTable ? 100 : undefined,
                          backgroundColor: row.level === 1 ? "#eaeeed" : (cIdx === 0 && isSocieTable ? "#fff" : undefined),
                          width: widthStyle,
                          minWidth: widthStyle,
                          maxWidth: widthStyle,
                        }}
                        className={`${
                          col.align === "right"
                            ? "ft-align-right"
                            : col.align === "center"
                            ? "ft-align-center"
                            : "ft-align-left"
                        } ${isEditing ? "ft-editing" : ""}`}
                          onClick={handleCellClick}
                          onDoubleClick={handleCellClick}
                        >
                          {col.type === "popup" && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  col.align === "right"
                                    ? "flex-end"
                                    : col.align === "center"
                                    ? "center"
                                    : "flex-start",
                                textAlign:
                                  col.align === "right"
                                    ? "right"
                                    : col.align === "center"
                                    ? "center"
                                    : "left",
                                width: "100%",
                              }}
                            >
                              <span
                                className={commoncss.clickable}
                                onClick={() => {
                                  handleChecklistModal(row);
                                }}
                              >
                                {row[col.field]}
                              </span>
                            </div>
                          )}
                          {col.type === "checklistref" && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  col.align === "right"
                                    ? "flex-end"
                                    : col.align === "center"
                                    ? "center"
                                    : "flex-start",
                                textAlign:
                                  col.align === "right"
                                    ? "right"
                                    : col.align === "center"
                                    ? "center"
                                    : "left",
                                width: "100%",
                              }}
                            >
                              {Array.isArray(row[col.field])
                                ? row[col.field].map((item, index) => (
                                    <span key={index}>
                                      <span
                                        onClick={() => {
                                          handleReferenceModal(item);
                                        }}
                                        style={{
                                          cursor: "pointer",
                                          marginRight: "8px",
                                          textDecoration: "underline",
                                        }}
                                      >
                                        {item}
                                      </span>
                                      {index < row[col.field].length - 1 && ", "}
                                    </span>
                                  ))
                                : row[col.field] ?? ""}
                            </div>
                          )}
                          {col.type === "redirect" && (
                            <div
                              style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                              textAlign:
                                col.align === "right"
                                  ? "right"
                                  : col.align === "center"
                                  ? "center"
                                  : "left",
                              width: "100%",
                            }}
                            >
                              {row[col.field] ? (
                                <span
                                  style={{
                                    color: "#205a46",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                  onClick={() => {
                                    redirectTo(row[col.field], row);
                                  }}
                                >
                                  {row[col.field]}
                                </span>
                              ) : (
                                <span style={{ color: "#ccc" }}>{row.level === 1 ? "" : "–"}</span>
                              )}
                            </div>
                          )}
                          {col.type === "checkbox" ? (
                            <div
                              style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                col.align === "right"
                                  ? "flex-end"
                                  : col.align === "center"
                                  ? "center"
                                  : "flex-start",
                              textAlign:
                                col.align === "right"
                                  ? "right"
                                  : col.align === "center"
                                  ? "center"
                                  : "left",
                              width: "100%",
                            }}
                            >
                              <input
                                name={`chk_${rowKey}_${col.field}_${col.optionValue}`}
                                className="ft-checkbox"
                                type="checkbox"
                                checked={row[col.field] === col.optionValue}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  toggleSingleValueCheckbox(rIdx, col, checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`${col.label} checkbox row ${rIdx}`}
                              />
                            </div>
                          ) : (
                            <>
                              {col.type !== "redirect" &&
                                col.type !== "popup" &&
                                col.type !== "checklistref" && (
                                  <div
                                    style={{
                                      visibility: isEditing ? "hidden" : "visible",
                                      textAlign: col.align || "left",
                                      width: "100%",
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: isEmptyPlaceholder ? "#aaa" : col.field === "notes" ? "#205a46" : "inherit",
                                        fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                                        fontWeight: col.field === "notes" ? 700 : "inherit",
                                        textDecoration: col.field === "notes" && !isEmptyPlaceholder ? "underline" : "none",
                                      }}
                                    >
                                      {cellText}
                                    </span>
                                  </div>
                                )}

                              {isEditing &&
                                col.type !== "redirect" &&
                                col.type !== "popup" &&
                                col.type !== "checklistref" && (
                                  <input
                                    ref={inputRef}
                                    className={`ft-edit-input ${inputAlignClass}`}
                                    type={
                                      col.type === "number" ? "number" : "text"
                                    }
                                    defaultValue={
                                      col.type === "array" &&
                                      Array.isArray(row[col.field])
                                        ? row[col.field].join(", ")
                                        : row[col.field] ?? ""
                                    }
                                    placeholder={
                                      row.isNewRow
                                        ? isValueColumn(col)
                                          ? "0"
                                          : "Enter text"
                                        : col.placeholder ?? ""
                                    }
                                    onBlur={(e) => commitValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        commitValue(e.target.value);
                                      if (e.key === "Escape") cancel();
                                    }}
                                    onKeyUp={(e) => {
                                      const raw = e.target.value;
                                      let outgoing = raw;
                                      if (col.type === "number") {
                                        const n =
                                          raw === "" ? null : Number(raw);
                                        outgoing = isNaN(n) ? null : n;
                                      } else if (col.type === "array") {
                                        outgoing = String(raw)
                                          .split(",")
                                          .map((v) => v.trim())
                                          .filter(Boolean);
                                      }
                                      const updated = [...localRows];
                                      updated[rIdx] = {
                                        ...updated[rIdx],
                                        [col.field]: outgoing,
                                      };
                                      setLocalRows(updated);
                                      emitImmediateChange(
                                        rIdx,
                                        col.field,
                                        outgoing
                                      );
                                    }}
                                  />
                                )}
                            </>
                          )}
                        </td>
                      );
                   })}
                    </>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
          )}
          {allowDragAndAdd && (
            <tfoot>
              <tr>
                <td colSpan={columns.length} style={{ border: "none", padding: "8px 0" }}>
                  <button
                    type="button"
                    onClick={addNewRow}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#205a46",
                      cursor: "pointer",
                      fontWeight: "500",
                      padding: "8px 12px 8px 0px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <PlusIcon style={{ width: 14, height: 14 }} /> Add Line Item
                  </button>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showChecklistModal && modalData && (
        <ChecklistModal
          setShowChecklistModal={setShowChecklistModal}
          modalData={modalData}
        />
      )}
      {viewReferenceModal && refrenceModalData && (
        <ReferenceModal
          setViewReferenceModal={setViewReferenceModal}
          modalData={refrenceModalData}
        />
      )}
    </div>
  );
}